import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './add-to-list-layer.scss?inline';
import { Messages, WishlistCollection } from '../../../@types/wishlist.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { collectionsContext, productsContext } from '../../../scripts/context/wishlistContext.js';
import { CollectionsState, ProductsState } from '../../../scripts/context/wishlist-state.js';
import { addToListLayerContext, AddToListLayerState } from '../../../scripts/context/addToListLayerContext.js';
import '../upsert-collection-layer/upsert-collection-layer.js';
import { repeat } from 'lit/directives/repeat.js';
import { collectionApi } from '../../../scripts/service/collection-api.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { pushToSnackbar } from '../../../scripts/service/snackbar-service.js';
import { productImageUrl } from '../../../scripts/service/image-formatter.js';
import { collectionMenuContext, productMenuContext } from '../../../scripts/context/menuContext.js';
import { CollectionMenuState, ProductMenuState } from '../../../scripts/context/menu-state.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';
import { getBackButton } from '../base-layer/base-layer.js';

@customElement('ents-add-to-list-layer')
export class AddToListLayerLayer extends LitElement {
  @consume({ context: addToListLayerContext, subscribe: true })
  @property({ attribute: false })
  private addToListLayerState!: AddToListLayerState;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: productMenuContext, subscribe: true })
  @property({ attribute: false })
  private productMenuState!: ProductMenuState;

  @consume({ context: collectionsContext, subscribe: true })
  @property({ attribute: false })
  private collectionsState!: CollectionsState;

  @consume({ context: collectionMenuContext, subscribe: true })
  @property({ attribute: false })
  private collectionMenuState!: CollectionMenuState;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: productsContext, subscribe: true })
  @property({ attribute: false })
  private productsState!: ProductsState;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(baseLayerStyles)];
  }

  private handleLayerClosed = () => {
    if (this.addToListLayerState.open) {
      this.addToListLayerState.update({ open: false });
    }
  };

  private openCreateCollectionLayer() {
    this.handleLayerClosed();
    this.collectionMenuState.openCreateLayer(this.productMenuState.context.product, this.open.bind(this));
  }

  private open() {
    this.addToListLayerState.update({ open: true });
  }

  private backButtonAction() {
    if (this.addToListLayerState.backButtonCallback) {
      this.addToListLayerState.backButtonCallback();
    }
    this.handleLayerClosed();
    this.dispatchEvent(new Event('ents-layer-back', { composed: true, bubbles: true }));
  }

  private addToList(collection: WishlistCollection) {
    this.addToListLayerState.update({ open: false });
    const isProductInCollection = this.productsState.isProductInCollection(
      ProductIdentifier.createByProduct(this.productMenuState.context.product),
      collection.id,
    );

    const absoluteImage = productImageUrl(
      '287x393_retina',
      this.productMenuState.context.product!.defaultImageUrls[0].url,
      'jpg',
    );
    const truncatedCollectionName =
      collection.name.length <= 30 ? collection.name : `${collection.name.substring(0, 30)}...`;
    if (isProductInCollection) {
      pushToSnackbar(`${this.messages['ents_wishlist_addToList_already_on_list']}`, absoluteImage);
    } else {
      this.addToListLayerState.update({ open: false });
      this.addToCollection(collection.id);
      pushToSnackbar(
        `${this.messages['ents_wishlist_addToList_on_list']}`.replace('{}', truncatedCollectionName),
        absoluteImage,
      );
    }
  }

  private addToCollection(collectionId: string) {
    const product = this.productMenuState.context.product!;
    collectionApi
      .addItem(this.wishlistAppConfig.collectionEndpoint, collectionId, product.syan, product.colorId)
      .then((res) => {
        if (res.isOk && res.collection) {
          this.productsState.addToCollection(product, res.collection, 'wishlist_action_menu');
          this.collectionsState.putToTop(res.collection);
        }
      });
  }

  protected render() {
    return html`
      <ents-base-layer
        drawer-test-id="ents-add-to-list-layer"
        ?showLayer="${this.addToListLayerState.open}"
        @ents-layer-closed="${this.handleLayerClosed}"
      >
        ${
          this.addToListLayerState.backButtonCallback === undefined
            ? nothing
            : getBackButton(this.messages, 'ents-add-to-list-layer', this.backButtonAction.bind(this))
        }
        <h3 class="ents-heading-medium" slot="title">${this.messages['ents_wishlist_addToList_choose_list']}</h3>
        <div class="ents-wishlist-atll">
          ${this.productMenuState.context.loggedIn ? this.renderContent() : this.renderLoginHint()}
        </div>
        <div slot="footer" class="ents-wishlist-atll__footer">
          ${
            this.productMenuState.context.loggedIn
              ? this.renderCreateCollectionFooterButton()
              : this.renderLoginFooterButton()
          }
        </div>
      </ents-base-layer>
    `;
  }

  private renderLoginHint() {
    return html`
      <p class="ents-copy" data-test-ents-add-to-list-layer-login-hint>
        ${this.messages['ents_wishlist_addToList_not_logged_in']}
      </p>
    `;
  }

  private renderCollectionsList() {
    return html`${repeat(
      this.collectionsState.entities,
      (col) => col.id,
      (col) => html`
        <button
          class="ents-copy ents-wishlist-atll-list__item fs-mask"
          @click="${this.addToList.bind(this, col)}"
          data-test-ents-add-to-list-layer-item-text
        >
          ${col.name}
        </button>
      `,
    )}`;
  }

  private renderEmptyCollectionsHint() {
    return html`
      <span data-test-ents-add-to-list-layer-empty-hint>${this.messages['ents_wishlist_addToList_no_lists']}</span>
    `;
  }

  private renderContent() {
    return html`
      ${this.collectionsState.entities.length === 0 ? this.renderEmptyCollectionsHint() : this.renderCollectionsList()}
    `;
  }

  private renderCreateCollectionFooterButton() {
    return html`
      <button
        class="bdk-button"
        type="button"
        variant="outline"
        size="medium"
        @click="${this.openCreateCollectionLayer}"
        data-test-ents-add-to-list-layer-create-button
      >
        ${this.messages['ents_wishlist_collection_create_title']}
      </button>
    `;
  }

  private renderLoginFooterButton() {
    return html`
      <a
        class="bdk-button"
        type="button"
        variant="outline"
        size="medium"
        href="${this.wishlistAppConfig.loginEndpointWithFavoritesReferrer}"
        data-test-ents-add-to-list-layer-login-link
      >
        ${this.messages['ents_wishlist_empty_logintext']}
      </a>
    `;
  }
}
